// src/pages/Settings.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { T, Card, Btn, SectionTitle } from '../components/UI';
import { changePassword, listUsers, resetPassword, getFinanceSettings, updateFinanceSettings } from '../services/api';

const inputStyle = {
  background: '#1a1a1a', border: `1px solid ${T.border}`, borderRadius: '8px',
  padding: '11px 14px', color: T.text, fontSize: '14px', width: '100%',
  outline: 'none', marginBottom: '12px', boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: '11px', color: T.muted, marginBottom: '5px', display: 'block',
  fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
};

export default function Settings() {
  const { can } = useAuth();

  return (
    <div>
      <SectionTitle sub="Manage your account and staff access">Settings</SectionTitle>
      <ChangePasswordCard />
      {can('manage_users') && <ResetPasswordCard />}
      {can('manage_users') && <FinanceSplitCard />}
    </div>
  );
}

function ChangePasswordCard() {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [msg, setMsg]             = useState(null); // { type: 'success'|'error', text }
  const [loading, setLoading]     = useState(false);

  const submit = async () => {
    setMsg(null);
    if (!currentPw || !newPw || !confirmPw) {
      return setMsg({ type: 'error', text: 'Please fill in all fields.' });
    }
    if (newPw.length < 8) {
      return setMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
    }
    if (newPw !== confirmPw) {
      return setMsg({ type: 'error', text: 'New password and confirmation do not match.' });
    }
    setLoading(true);
    try {
      const res = await changePassword(currentPw, newPw);
      setMsg({ type: 'success', text: res.data.message || 'Password changed successfully.' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to change password.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card style={{ maxWidth: '420px' }}>
      <div style={{ fontSize: '15px', fontWeight: 800, marginBottom: '4px' }}>Change your password</div>
      <div style={{ fontSize: '12px', color: T.muted, marginBottom: '16px' }}>
        You'll need your current password to set a new one.
      </div>

      <label style={labelStyle}>Current password</label>
      <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} style={inputStyle} />

      <label style={labelStyle}>New password</label>
      <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} style={inputStyle} placeholder="At least 8 characters" />

      <label style={labelStyle}>Confirm new password</label>
      <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && submit()} style={inputStyle} />

      {msg && (
        <div style={{ fontSize: '12px', marginBottom: '12px', color: msg.type === 'success' ? T.success : T.danger }}>
          {msg.text}
        </div>
      )}

      <Btn onClick={submit} disabled={loading} variant="gold">
        {loading ? 'Saving...' : 'Update password'}
      </Btn>
    </Card>
  );
}

function FinanceSplitCard() {
  const [fundPercent, setFundPercent] = useState('');
  const [members, setMembers] = useState('');
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    getFinanceSettings()
      .then(res => {
        const d = res.data.data;
        setFundPercent(String(d.business_fund_percent));
        setMembers(String(d.profit_pool_members));
      })
      .catch(() => setMsg({ type: 'error', text: 'Failed to load finance settings.' }))
      .finally(() => setLoadingInitial(false));
  }, []);

  const submit = async () => {
    setMsg(null);
    const pct = Number(fundPercent);
    const mem = Number(members);

    if (!Number.isFinite(pct) || pct < 0 || pct > 100)
      return setMsg({ type: 'error', text: 'Business fund % must be between 0 and 100.' });
    if (!Number.isInteger(mem) || mem < 1)
      return setMsg({ type: 'error', text: 'Profit pool members must be a positive whole number.' });

    setLoading(true);
    try {
      const res = await updateFinanceSettings(pct, mem);
      setMsg({ type: 'success', text: res.data.message || 'Settings updated.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to update settings.' });
    } finally {
      setLoading(false);
    }
  };

  const fundPct = Number(fundPercent) || 0;
  const profitPct = 100 - fundPct;

  return (
    <Card style={{ maxWidth: '420px' }}>
      <div style={{ fontSize: '15px', fontWeight: 800, marginBottom: '4px' }}>Finance split (Business Fund vs Profit Pool)</div>
      <div style={{ fontSize: '12px', color: T.muted, marginBottom: '16px' }}>
        Every confirmed payment is split between the Business Fund and the
        Profit Pool, which is then divided equally among staff. Changes
        apply to <b>future payments only</b> — past payments keep the split
        ratio that was in effect when they were recorded.
      </div>

      {loadingInitial ? (
        <div style={{ fontSize: '12px', color: T.muted }}>Loading...</div>
      ) : (
        <>
          <label style={labelStyle}>Business Fund share (%)</label>
          <input type="number" min="0" max="100" step="1" value={fundPercent}
            onChange={e => setFundPercent(e.target.value)} style={inputStyle} />

          <label style={labelStyle}>Profit Pool members (staff count)</label>
          <input type="number" min="1" step="1" value={members}
            onChange={e => setMembers(e.target.value)} style={inputStyle} />

          <div style={{ fontSize: '12px', color: T.muted, marginBottom: '12px' }}>
            Preview: <b>{fundPct}%</b> to Business Fund, <b>{profitPct}%</b> to Profit Pool
            {members ? ` (split ${profitPct}% ÷ ${members} = ${(profitPct / (Number(members) || 1)).toFixed(2)}% per person)` : ''}.
          </div>

          {msg && (
            <div style={{ fontSize: '12px', marginBottom: '12px', color: msg.type === 'success' ? T.success : T.danger }}>
              {msg.text}
            </div>
          )}

          <Btn onClick={submit} disabled={loading} variant="gold">
            {loading ? 'Saving...' : 'Save split settings'}
          </Btn>
        </>
      )}
    </Card>
  );
}

function ResetPasswordCard() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [newPw, setNewPw]   = useState('');
  const [msg, setMsg]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    listUsers()
      .then(res => setUsers(res.data.data))
      .catch(() => setMsg({ type: 'error', text: 'Failed to load staff list.' }))
      .finally(() => setLoadingUsers(false));
  }, []);

  const submit = async () => {
    setMsg(null);
    if (!selectedId) return setMsg({ type: 'error', text: 'Select a staff member.' });
    if (!newPw || newPw.length < 8) return setMsg({ type: 'error', text: 'New password must be at least 8 characters.' });

    setLoading(true);
    try {
      const res = await resetPassword(selectedId, newPw);
      setMsg({ type: 'success', text: res.data.message || 'Password reset.' });
      setNewPw('');
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to reset password.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card style={{ maxWidth: '420px' }}>
      <div style={{ fontSize: '15px', fontWeight: 800, marginBottom: '4px' }}>Reset a staff member's password</div>
      <div style={{ fontSize: '12px', color: T.muted, marginBottom: '16px' }}>
        As {user?.name}, you can set a new password for any staff member. Existing passwords
        can't be viewed — only replaced. Share the new password with them securely and ask
        them to change it again from their own Settings page once logged in.
      </div>

      {loadingUsers ? (
        <div style={{ fontSize: '12px', color: T.muted, marginBottom: '12px' }}>Loading staff list...</div>
      ) : (
        <>
          <label style={labelStyle}>Staff member</label>
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)} style={inputStyle}>
            <option value="">Select staff member...</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
            ))}
          </select>

          <label style={labelStyle}>New password</label>
          <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()} style={inputStyle} placeholder="At least 8 characters" />

          {msg && (
            <div style={{ fontSize: '12px', marginBottom: '12px', color: msg.type === 'success' ? T.success : T.danger }}>
              {msg.text}
            </div>
          )}

          <Btn onClick={submit} disabled={loading} variant="gold">
            {loading ? 'Saving...' : 'Reset password'}
          </Btn>
        </>
      )}
    </Card>
  );
}
